import React, { useState, useEffect } from 'react';
import {
  Award,
  Plus,
  Search,
  CheckCircle2,
  Building2,
  FileCheck,
  Calendar,
  User,
  ArrowRight,
  MessageSquare,
  ShieldCheck,
  Clock,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { CompletionCertificateItem, PhysicalCompletionCertificateItem } from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';

interface CompletionPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const CompletionPage: React.FC<CompletionPageProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'PCC' | 'CC'>('PCC');

  const [pccList, setPccList] = useState<PhysicalCompletionCertificateItem[]>([]);
  const [certificates, setCertificates] = useState<CompletionCertificateItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [showAddPccModal, setShowAddPccModal] = useState(false);
  const [showAddCcModal, setShowAddCcModal] = useState(false);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  // PCC Form
  const [pccForm, setPccForm] = useState({
    externalPccNumber: '',
    projectId: '',
    branchId: '',
    ticketId: '',
    certificateDate: new Date().toISOString().split('T')[0],
    receiptDate: new Date().toISOString().split('T')[0],
    receivedByEmployee: 'Engr. Bilal Hashmi',
    coveredScope: 'Complete branch floor tile laying, partitions, and cash counter handover.',
    remarks: 'Physical completion certificate stamped by UBL branch manager.',
  });

  // CC Form
  const [ccForm, setCcForm] = useState({
    projectId: '',
    branchId: '',
    ticketId: '',
    poNumber: '',
    workDescription: '',
    completionDate: new Date().toISOString().split('T')[0],
    engineer: 'Engr. Tariq Mehmood',
    ublRepresentative: 'Engr. Imran Qureshi (UBL Facility Manager)',
    remarks: 'Joint site inspection completed with no defects observed.',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pccRes, ccRes, pRes, bRes] = await Promise.all([
        fetch('/api/pcc'),
        fetch('/api/completion-certificates'),
        fetch('/api/projects'),
        fetch('/api/branches'),
      ]);

      if (pccRes.ok) {
        const pccData = await pccRes.json();
        if (Array.isArray(pccData)) setPccList(pccData);
      }
      if (ccRes.ok) {
        const ccData = await ccRes.json();
        if (Array.isArray(ccData)) setCertificates(ccData);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) {
            setPccForm((prev) => ({
              ...prev,
              projectId: String(pData[0].id),
              branchId: String(pData[0].branchId),
              ticketId: pData[0].ticketId ? String(pData[0].ticketId) : '',
            }));
            setCcForm((prev) => ({
              ...prev,
              projectId: String(pData[0].id),
              branchId: String(pData[0].branchId),
              poNumber: pData[0].poNumber || '',
              ticketId: pData[0].ticketId ? String(pData[0].ticketId) : '',
            }));
          }
        }
      }
      if (bRes.ok) {
        const bData = await bRes.json();
        if (Array.isArray(bData)) setBranches(bData);
      }
    } catch (err) {
      console.warn('Completion data fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreatePcc = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/pcc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pccForm),
      });
      if (res.ok) {
        setShowAddPccModal(false);
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleVerifyPcc = async (id: number) => {
    try {
      const res = await fetch(`/api/pcc/${id}/verify`, { method: 'PUT' });
      if (res.ok) fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRejectPcc = async (id: number) => {
    const reason = prompt('Please enter the reason for rejection:');
    if (!reason) return;
    try {
      const res = await fetch(`/api/pcc/${id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejectionReason: reason }),
      });
      if (res.ok) fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateCc = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/completion-certificates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ccForm),
      });
      if (res.ok) {
        setShowAddCcModal(false);
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Physical Completion Certificates (PCC) Register
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-200">
              Mandatory Invoice Gating
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Physical completion certificates tracking and verification workflow. Only verified PCC records allow UBL sales invoice submission.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === 'PCC' && (
            <button
              onClick={() => setShowAddPccModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-4 h-4 text-emerald-200" />
              <span>Register Received PCC</span>
            </button>
          )}
          {activeTab === 'CC' && (
            <button
              onClick={() => setShowAddCcModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-4 h-4 text-amber-400" />
              <span>Issue Joint CC</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 text-xs font-bold">
        <button
          onClick={() => setActiveTab('PCC')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'PCC'
              ? 'border-emerald-600 text-emerald-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          <span>Physical Completion Certificates (PCC) Register ({pccList.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('CC')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'CC'
              ? 'border-blue-600 text-blue-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Award className="w-4 h-4" />
          <span>Joint Completion Certificates ({certificates.length})</span>
        </button>
      </div>

      {/* TAB 1: PCC REGISTER */}
      {activeTab === 'PCC' && (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0" />
            <div>
              <strong>Audit Rule:</strong> A physical completion certificate must transition to <strong>VERIFIED</strong> before the Accounts department can generate or submit an invoice to UBL. Unverified or rejected certificates block invoice creation.
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">PCC Number</th>
                    <th className="py-3 px-4">Bank Ref #</th>
                    <th className="py-3 px-4">Target Branch &amp; Project</th>
                    <th className="py-3 px-4">Receipt Date</th>
                    <th className="py-3 px-4">Covered Scope</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Verification Audit</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pccList.map((pcc) => (
                    <tr key={pcc.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-bold text-emerald-950">{pcc.pccNumber}</td>
                      <td className="py-3 px-4 font-mono text-slate-700">{pcc.externalPccNumber || 'N/A'}</td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">{pcc.branchName || `Branch #${pcc.branchId}`}</div>
                        <div className="text-[11px] text-slate-500">{pcc.projectName || `Project #${pcc.projectId}`}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{pcc.receiptDate}</td>
                      <td className="py-3 px-4 text-slate-700 max-w-xs truncate" title={pcc.coveredScope || ''}>
                        {pcc.coveredScope || 'Standard milestone sign-off'}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            pcc.status === 'VERIFIED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : pcc.status === 'REJECTED'
                              ? 'bg-rose-100 text-rose-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {pcc.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {pcc.verifiedBy ? (
                          <div className="text-[11px]">
                            <div className="font-semibold text-emerald-800">{pcc.verifiedBy}</div>
                            <div className="text-slate-400">{pcc.verifiedAt ? pcc.verifiedAt.split('T')[0] : ''}</div>
                          </div>
                        ) : (
                          <span className="text-amber-700 text-[11px]">Pending Joint Review</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right space-x-1">
                        {pcc.status !== 'VERIFIED' && (
                          <button
                            onClick={() => handleVerifyPcc(pcc.id)}
                            className="px-2.5 py-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded text-[11px] font-bold shadow-2xs"
                          >
                            Verify PCC
                          </button>
                        )}
                        {pcc.status !== 'REJECTED' && pcc.status !== 'VERIFIED' && (
                          <button
                            onClick={() => handleRejectPcc(pcc.id)}
                            className="px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded text-[11px] font-bold shadow-2xs"
                          >
                            Reject
                          </button>
                        )}
                        {pcc.status === 'VERIFIED' && (
                          <button
                            onClick={() => onNavigate('invoices')}
                            className="px-2.5 py-1 bg-blue-700 hover:bg-blue-800 text-white rounded text-[11px] font-bold shadow-2xs inline-flex items-center gap-1"
                          >
                            <span>Create Invoice</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: JOINT COMPLETION CERTIFICATES */}
      {activeTab === 'CC' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">Certificate #</th>
                  <th className="py-3 px-4">Branch &amp; Project</th>
                  <th className="py-3 px-4">PO Reference</th>
                  <th className="py-3 px-4">Completion Date</th>
                  <th className="py-3 px-4">Engineer / UBL Signatory</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Dispatch</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {certificates.map((cert) => (
                  <tr key={cert.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4 font-bold text-blue-900">{cert.certificateNumber}</td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-900">{cert.branchName || `Branch #${cert.branchId}`}</div>
                      <div className="text-[11px] text-slate-500">{cert.projectName}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-mono">{cert.poNumber || 'N/A'}</td>
                    <td className="py-3 px-4 text-slate-600">{cert.completionDate}</td>
                    <td className="py-3 px-4 text-slate-700">
                      <div>{cert.engineer}</div>
                      <div className="text-[11px] text-slate-500">{cert.ublRepresentative}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        {cert.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setShareData({
                            title: `Completion Certificate - ${cert.certificateNumber}`,
                            docType: 'COMPLETION_CERT',
                            referenceNumber: cert.certificateNumber,
                            projectName: cert.projectName,
                            recipientName: cert.ublRepresentative,
                            date: cert.completionDate,
                            detailsSummary: `Joint Completion Certificate issued for ${cert.branchName}. Engineer: ${cert.engineer}. UBL Rep: ${cert.ublRepresentative}. Status: APPROVED.`,
                          });
                          setWhatsAppModalOpen(true);
                        }}
                        className="text-xs font-bold text-[#075e54] hover:text-white flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366] px-2.5 py-1.5 rounded-lg transition-colors ml-auto"
                        title="Send certificate via WhatsApp"
                      >
                        <MessageSquare className="w-3.5 h-3.5 text-[#25D366] group-hover:text-white" />
                        <span>WhatsApp CC</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL 1: REGISTER PCC */}
      {showAddPccModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Register Physical Completion Certificate</h3>
                <p className="text-xs text-slate-500">Official handover document signed by UBL branch manager</p>
              </div>
              <button
                onClick={() => setShowAddPccModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreatePcc} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Target Project</label>
                  <select
                    value={pccForm.projectId}
                    onChange={(e) => {
                      const sel = projects.find((p) => String(p.id) === e.target.value);
                      setPccForm({
                        ...pccForm,
                        projectId: e.target.value,
                        branchId: sel ? String(sel.branchId) : pccForm.branchId,
                        ticketId: sel?.ticketId ? String(sel.ticketId) : '',
                      });
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  >
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.projectNumber} - {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">UBL Branch</label>
                  <select
                    value={pccForm.branchId}
                    onChange={(e) => setPccForm({ ...pccForm, branchId: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  >
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branchCode} - {b.branchName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Bank Physical PCC Ref #</label>
                  <input
                    type="text"
                    placeholder="e.g. UBL-PCC-2026-088"
                    value={pccForm.externalPccNumber}
                    onChange={(e) => setPccForm({ ...pccForm, externalPccNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Received by Staff</label>
                  <input
                    type="text"
                    value={pccForm.receivedByEmployee}
                    onChange={(e) => setPccForm({ ...pccForm, receivedByEmployee: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Certificate Date</label>
                  <input
                    type="date"
                    value={pccForm.certificateDate}
                    onChange={(e) => setPccForm({ ...pccForm, certificateDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Receipt Date</label>
                  <input
                    type="date"
                    value={pccForm.receiptDate}
                    onChange={(e) => setPccForm({ ...pccForm, receiptDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Covered Scope &amp; Deliverables</label>
                <textarea
                  rows={2}
                  value={pccForm.coveredScope}
                  onChange={(e) => setPccForm({ ...pccForm, coveredScope: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddPccModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-2xs"
                >
                  Register PCC
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: ADD CC */}
      {showAddCcModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Issue Joint Completion Certificate</h3>
              <button
                onClick={() => setShowAddCcModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateCc} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Target Project</label>
                <select
                  value={ccForm.projectId}
                  onChange={(e) => {
                    const sel = projects.find((p) => String(p.id) === e.target.value);
                    setCcForm({
                      ...ccForm,
                      projectId: e.target.value,
                      branchId: sel ? String(sel.branchId) : ccForm.branchId,
                      poNumber: sel?.poNumber || ccForm.poNumber,
                      ticketId: sel?.ticketId ? String(sel.ticketId) : '',
                    });
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectNumber} - {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">PO Reference #</label>
                <input
                  type="text"
                  value={ccForm.poNumber}
                  onChange={(e) => setCcForm({ ...ccForm, poNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Work Description</label>
                <textarea
                  rows={2}
                  value={ccForm.workDescription}
                  onChange={(e) => setCcForm({ ...ccForm, workDescription: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="Completed civil, electrical and finishing works according to approved BOQ."
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddCcModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold shadow-2xs"
                >
                  Issue Certificate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {shareData && (
        <WhatsAppShareModal
          isOpen={whatsAppModalOpen}
          onClose={() => setWhatsAppModalOpen(false)}
          data={shareData}
        />
      )}
    </div>
  );
};
